import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import { apiService } from "../../services/apiService";
import { API_ENDPOINTS, buildApiUrl } from "../../services/apiEndpoints";

const SubscriptionList = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: null,
    status: null,
    start_date_from: null,
    start_date_to: null,
    firm_name: "",
    mobile_number: "",
  });

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "buyer", label: "Buyer" },
    { value: "seller", label: "Seller" },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "expired", label: "Expired" },
  ];

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      border: "1px solid #ced4da",
      borderRadius: "0.375rem",
      fontSize: "14px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#495057",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#007bff"
        : state.isFocused
        ? "#f8f9fa"
        : "white",
      color: state.isSelected ? "white" : "#495057",
      fontSize: "14px",
    }),
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.type?.value) queryParams.append("type", filters.type.value);
      if (filters.status?.value)
        queryParams.append("status", filters.status.value);
      if (filters.start_date_from) {
        const dateFrom = new Date(filters.start_date_from);
        const formattedDateFrom = dateFrom.getFullYear() + '-' + 
          String(dateFrom.getMonth() + 1).padStart(2, '0') + '-' + 
          String(dateFrom.getDate()).padStart(2, '0');
        queryParams.append("start_date_from", formattedDateFrom);
      }
      if (filters.start_date_to) {
        const dateTo = new Date(filters.start_date_to);
        const formattedDateTo = dateTo.getFullYear() + '-' + 
          String(dateTo.getMonth() + 1).padStart(2, '0') + '-' + 
          String(dateTo.getDate()).padStart(2, '0');
        queryParams.append("start_date_to", formattedDateTo);
      }
      if (filters.firm_name && filters.firm_name.trim() !== "")
        queryParams.append("firm_name", filters.firm_name.trim());
      if (filters.mobile_number && filters.mobile_number.trim() !== "")
        queryParams.append("mobile_number", filters.mobile_number.trim());
      const endpoint = API_ENDPOINTS.SUBSCRIPTIONS.LIST;
      const url = queryParams.toString()
        ? `${endpoint}?${queryParams.toString()}`
        : endpoint;
      const response = await apiService.get(url);

      if (response) {
        const allSubscriptions = [
          ...(response.buyer_subscriptions || []).map((sub) => ({
            ...sub,
            subscription_type: "buyer",
          })),
          ...(response.seller_subscriptions || []).map((sub) => ({
            ...sub,
            subscription_type: "seller",
          })),
        ];
        setSubscriptions(allSubscriptions);
      } else {
        toast.error("Failed to fetch subscriptions");
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      toast.error("Failed to fetch subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (selectedOption, filterName) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: selectedOption,
    }));
    const newFilters = {
      ...filters,
      [filterName]: selectedOption,
    };
    fetchSubscriptionsWithFilters(newFilters);
  };

  const fetchSubscriptionsWithFilters = async (currentFilters) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (currentFilters.type?.value)
        queryParams.append("type", currentFilters.type.value);
      if (currentFilters.status?.value)
        queryParams.append("status", currentFilters.status.value);

      if (currentFilters.start_date_from) {
        const dateFrom = new Date(currentFilters.start_date_from);
        const formattedDateFrom = dateFrom.getFullYear() + '-' + 
          String(dateFrom.getMonth() + 1).padStart(2, '0') + '-' + 
          String(dateFrom.getDate()).padStart(2, '0');
        queryParams.append("start_date_from", formattedDateFrom);
      }

      if (currentFilters.start_date_to) {
        const dateTo = new Date(currentFilters.start_date_to);
        const formattedDateTo = dateTo.getFullYear() + '-' + 
          String(dateTo.getMonth() + 1).padStart(2, '0') + '-' + 
          String(dateTo.getDate()).padStart(2, '0');
        queryParams.append("start_date_to", formattedDateTo);
      }
      if (currentFilters.firm_name && currentFilters.firm_name.trim() !== "")
        queryParams.append("firm_name", currentFilters.firm_name.trim());
      if (currentFilters.mobile_number && currentFilters.mobile_number.trim() !== "")
        queryParams.append("mobile_number", currentFilters.mobile_number.trim());

      const endpoint = API_ENDPOINTS.SUBSCRIPTIONS.LIST;
      const url = queryParams.toString()
        ? `${endpoint}?${queryParams.toString()}`
        : endpoint;

      const response = await apiService.get(url);

      if (response) {
        const allSubscriptions = [
          ...(response.buyer_subscriptions || []).map((sub) => ({
            ...sub,
            subscription_type: "buyer",
          })),
          ...(response.seller_subscriptions || []).map((sub) => ({
            ...sub,
            subscription_type: "seller",
          })),
        ];
        setSubscriptions(allSubscriptions);
      } else {
        toast.error("Failed to fetch subscriptions");
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      toast.error("Failed to fetch subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.type?.value) queryParams.append("type", filters.type.value);
      if (filters.status?.value)
        queryParams.append("status", filters.status.value);
      if (filters.start_date_from) {
        const dateFrom = new Date(filters.start_date_from);
        const formattedDateFrom = dateFrom.getFullYear() + '-' + 
          String(dateFrom.getMonth() + 1).padStart(2, '0') + '-' + 
          String(dateFrom.getDate()).padStart(2, '0');
        queryParams.append("start_date_from", formattedDateFrom);
      }
      if (filters.start_date_to) {
        const dateTo = new Date(filters.start_date_to);
        const formattedDateTo = dateTo.getFullYear() + '-' + 
          String(dateTo.getMonth() + 1).padStart(2, '0') + '-' + 
          String(dateTo.getDate()).padStart(2, '0');
        queryParams.append("start_date_to", formattedDateTo);
      }
      if (filters.firm_name && filters.firm_name.trim() !== "")
        queryParams.append("firm_name", filters.firm_name.trim());
      if (filters.mobile_number && filters.mobile_number.trim() !== "")
        queryParams.append("mobile_number", filters.mobile_number.trim());

      const endpoint = API_ENDPOINTS.SUBSCRIPTIONS.LIST_EXPORT;
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://prod.api.morbibazaar.com/api/v1/";
      const url = queryParams.toString()
        ? `${baseUrl}${endpoint}?${queryParams.toString()}`
        : `${baseUrl}${endpoint}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const contentType = response.headers.get("content-type");
        let fileExtension = ".xlsx";

        if (contentType) {
          if (contentType.includes("csv")) {
            fileExtension = ".csv";
          } else if (contentType.includes("pdf")) {
            fileExtension = ".pdf";
          } else if (
            contentType.includes("excel") ||
            contentType.includes("spreadsheet")
          ) {
            fileExtension = ".xlsx";
          }
        }
        const now = new Date();
        const dateStr = now.getFullYear() + '-' + 
          String(now.getMonth() + 1).padStart(2, '0') + '-' + 
          String(now.getDate()).padStart(2, '0');
        const typeStr = filters.type?.value ? `_${filters.type.value}` : "";
        const statusStr = filters.status?.value
          ? `_${filters.status.value}`
          : "";
        link.download = `subscriptions_${dateStr}${typeStr}${statusStr}${fileExtension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.success("Export completed successfully");
      } else {
        const errorText = await response.text();
        console.error("Export API Error:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`
        );
      }
    } catch (error) {
      console.error("Error exporting subscriptions:", error);
      toast.error("Failed to export subscriptions");
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price, currency) => {
    return `${currency} ${parseFloat(price).toFixed(2)}`;
  };

  const getStatusBadge = (isActive, endDate) => {
    const now = new Date();
    const end = new Date(endDate);

    if (!isActive || end < now) {
      return <span className="badge bg-danger">Expired</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  const renderSubscriptionTable = () => {
    if (!subscriptions || subscriptions.length === 0) {
      return (
        <tr>
          <td colSpan="9" className="text-center">
            No subscriptions found
          </td>
        </tr>
      );
    }

    return subscriptions.map((subscription, index) => (
      <tr key={subscription.id}>
        <td>{index + 1}</td>
        <td>
          <span
            className={`badge ${
              subscription.subscription_type === "buyer"
                ? "bg-primary"
                : "bg-success"
            }`}
          >
            {subscription.subscription_type === "buyer" ? "Buyer" : "Seller"}
          </span>
        </td>
        <td>
          <strong>{subscription.plan?.name || "N/A"}</strong>
          <br />
          <small>{subscription.plan?.description || "No description"}</small>
        </td>
        <td>
          <span className="badge bg-info">
            {formatPrice(subscription.plan?.price, subscription.plan?.currency)}
          </span>
          <br />
          <small>{subscription.plan?.duration_months} month(s)</small>
        </td>
        <td>
          {subscription.buyer ? (
            <>
              <strong>
                {subscription.buyer.user?.buyer_profile?.firm_name || "N/A"}
              </strong>
              <br />
              <small>
                Owner:{" "}
                {subscription.buyer.user?.buyer_profile?.owner_name || "N/A"}
              </small>
              <br />
              <small>
                Mobile: {subscription.buyer.user?.mobile_number || "N/A"}
              </small>
            </>
          ) : (
            <>
              <strong>
                {subscription.seller?.user?.seller_profile?.firm_name || "N/A"}
              </strong>
              <br />
              <small>
                Owner:{" "}
                {subscription.seller?.user?.seller_profile?.owner_name || "N/A"}
              </small>
              <br />
              <small>
                Mobile: {subscription.seller?.user?.mobile_number || "N/A"}
              </small>
            </>
          )}
        </td>
        <td>{formatDate(subscription.start_date)}</td>
        <td>{formatDate(subscription.end_date)}</td>
        <td>{getStatusBadge(subscription.is_active, subscription.end_date)}</td>
        <td>
          <span className="badge bg-secondary">
            {subscription.transaction || "N/A"}
          </span>
        </td>
      </tr>
    ));
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  return (
    <>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Subscription List</h3>
          </div>
          <div className="col-auto text-end float-end ms-auto">
            <button
              className="btn btn-success"
              onClick={handleExport}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="fe fe-download"></i> Export
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Filters</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Type</label>
                    <Select
                      value={filters.type}
                      onChange={(selectedOption) =>
                        handleFilterChange(selectedOption, "type")
                      }
                      options={typeOptions}
                      placeholder="Select Type"
                      isClearable
                      styles={customSelectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <Select
                      value={filters.status}
                      onChange={(selectedOption) =>
                        handleFilterChange(selectedOption, "status")
                      }
                      options={statusOptions}
                      placeholder="Select Status"
                      isClearable
                      styles={customSelectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Start Date From</label>
                    <DatePicker
                      placeholder="yyyy-MM-dd"
                      value={filters.start_date_from}
                      onChange={(date) => {
                        const newFilters = {
                          ...filters,
                          start_date_from: date,
                        };
                        setFilters(newFilters);
                        fetchSubscriptionsWithFilters(newFilters);
                      }}
                      format="yyyy-MM-dd"
                      clearIcon={null}
                      className="form-control"
                      calendarIcon={<i className="fe fe-calendar"></i>}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Start Date To</label>
                    <DatePicker
                      value={filters.start_date_to}
                      onChange={(date) => {
                        const newFilters = { ...filters, start_date_to: date };
                        setFilters(newFilters);
                        fetchSubscriptionsWithFilters(newFilters);
                      }}
                      format="yyyy-MM-dd"
                      clearIcon={null}
                      className="form-control"
                      calendarIcon={<i className="fe fe-calendar"></i>}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Firm Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter firm name"
                      value={filters.firm_name}
                      onChange={(e) => {
                        const newFilters = { ...filters, firm_name: e.target.value };
                        setFilters(newFilters);
                        fetchSubscriptionsWithFilters(newFilters);
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Mobile Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter mobile number"
                      value={filters.mobile_number}
                      onChange={(e) => {
                        const newFilters = { ...filters, mobile_number: e.target.value };
                        setFilters(newFilters);
                        fetchSubscriptionsWithFilters(newFilters);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* All Subscriptions */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">
                All Subscriptions
                <span className="badge bg-primary ms-2">
                  {subscriptions?.length || 0}
                </span>
              </h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Type</th>
                        <th>Plan Details</th>
                        <th>Price & Duration</th>
                        <th>User Details</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Status</th>
                        <th>Transaction ID</th>
                      </tr>
                    </thead>
                    <tbody>{renderSubscriptionTable()}</tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionList;
